/**
 * JobTemplateEditor — unified surface for New / Edit / View modes.
 *
 * One shell, one layout, mode-conditional treatment. Replaces the divergence
 * between the old JobTemplateForm (admin-panel feel) and JobTemplateSystemView
 * (documentation feel) so all three template surfaces read consistently.
 *
 * Layout matches JobNewPage: two-column body with primary fields on the
 * left and Details on the right. The right column shows Defaults +
 * Reference files; in View mode it also shows existing schemas as code
 * blocks. Schema editing is intentionally not exposed in V1 — Edit/New
 * preserve the schema fields the template was created with but don't
 * surface JSON editors for them.
 */

import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert } from '@/components/Alert/Alert';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { ChipMulti } from '@/components/ChipMulti/ChipMulti';
import { TextField } from '@/components/TextField/TextField';
import { RunJobSheet } from '@/features/jobs/components/RunJobSheet';
import { usePositionsCatalog, useUnitsCatalog } from '@/features/members';
import { ApiError, NetworkError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useUploadAttachment } from '@/features/attachments/hooks';
import {
  useCreateJobTemplate,
  useCustomizeJobTemplate,
  useJobTemplateAttachments,
  useLinkJobTemplateAttachment,
  useUnlinkJobTemplateAttachment,
  useUpdateJobTemplate,
} from '../hooks';
import {
  jobTemplateFormSchema,
  type JobTemplateCreateBody,
  type JobTemplateDetail,
  type JobTemplateFormValues,
} from '../schemas';
import { DefaultLinks } from './DefaultLinks';
import styles from './JobTemplateEditor.module.css';

type Mode = 'new' | 'edit' | 'view';

type TenantDetail = Extract<JobTemplateDetail, { kind: 'tenant' }>;
type SystemDetail = Extract<JobTemplateDetail, { kind: 'system' }>;

interface JobTemplateEditorProps {
  mode: Mode;
  /** Required for edit + view modes. Undefined for new. */
  template?: TenantDetail | SystemDetail;
}

export function JobTemplateEditor({ mode, template }: JobTemplateEditorProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { customizedFrom?: string } | null;
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [runOpen, setRunOpen] = useState(false);

  // Default Position / Unit assignments kept as separate state (M:N, not
  // part of the form schema). Initialized from the loaded template; submit
  // sends the full lists for reconciliation server-side.
  const [positionIds, setPositionIds] = useState<string[]>(
    template?.defaultPositions.map((p) => p.id) ?? [],
  );
  const [unitIds, setUnitIds] = useState<string[]>(template?.defaultUnits.map((u) => u.id) ?? []);

  const create = useCreateJobTemplate();
  const update = useUpdateJobTemplate();
  const customize = useCustomizeJobTemplate();
  const positionsCatalog = usePositionsCatalog();
  const unitsCatalog = useUnitsCatalog();

  // RHF form is only meaningful in new/edit. We still construct it in view
  // mode (harmless) so the hook list stays stable across renders.
  const form = useForm<JobTemplateFormValues>({
    resolver: zodResolver(jobTemplateFormSchema),
    defaultValues: {
      title: template?.title ?? '',
      description: template?.description ?? '',
      instructions: template?.instructions ?? '',
      inputsDescription: template?.inputsDescription ?? '',
      outputsDescription: template?.outputsDescription ?? '',
      // V1: schema editing isn't exposed. Carry the existing values through
      // so an Edit doesn't blow away schemas set by a customize-from-system.
      inputSchemaText: template?.inputSchema ? JSON.stringify(template.inputSchema, null, 2) : '',
      outputSchemaText: template?.outputSchema
        ? JSON.stringify(template.outputSchema, null, 2)
        : '',
      validateInputs: template?.validateInputs ?? false,
      validateOutputs: template?.validateOutputs ?? false,
      outputValidationMode: template?.outputValidationMode ?? 'strict',
    },
  });

  function onSubmit(values: JobTemplateFormValues) {
    setFormError(null);
    setFieldErrors({});

    // Schemas are preserved-but-not-edited; parse what's there (will be the
    // template's existing JSON or empty).
    let inputSchema: unknown = null;
    let outputSchema: unknown = null;
    try {
      inputSchema = values.inputSchemaText?.trim() ? JSON.parse(values.inputSchemaText) : null;
      outputSchema = values.outputSchemaText?.trim() ? JSON.parse(values.outputSchemaText) : null;
    } catch {
      setFormError(t('Saved schemas became invalid. Edit them through the API for now.'));
      return;
    }

    const body: JobTemplateCreateBody = {
      title: values.title,
      description: values.description?.trim() ? values.description : null,
      instructions: values.instructions,
      inputs_description: values.inputsDescription?.trim() ? values.inputsDescription : null,
      outputs_description: values.outputsDescription?.trim() ? values.outputsDescription : null,
      input_schema: inputSchema,
      output_schema: outputSchema,
      validate_inputs: inputSchema ? values.validateInputs : false,
      validate_outputs: outputSchema ? values.validateOutputs : false,
      output_validation_mode: values.outputValidationMode,
    };

    if (mode === 'edit' && template) {
      update.mutate(
        {
          id: template.id,
          patch: {
            ...body,
            default_position_ids: positionIds,
            default_unit_ids: unitIds,
          },
        },
        { onSuccess: backToList, onError: handleApiError },
      );
    } else {
      // Note: new templates can't carry default assignments — the join
      // tables require an existing template id. Save first, then assign via
      // Edit. The picker is hidden in `new` mode to avoid suggesting
      // otherwise.
      create.mutate(body, { onSuccess: backToList, onError: handleApiError });
    }
  }

  function backToList() {
    void navigate('/library/job-profiles');
  }

  function handleApiError(err: unknown) {
    if (err instanceof ApiError) {
      const issues = (err.details as { issues?: { path: string; message: string }[] } | undefined)
        ?.issues;
      if (issues && issues.length > 0) {
        const next: Record<string, string> = {};
        for (const i of issues) {
          if (i.path === '/title') next.title = i.message;
          else if (i.path === '/instructions') next.instructions = i.message;
          else setFormError(i.message);
        }
        setFieldErrors(next);
        return;
      }
      if (err.code === 'SYSTEM_ROW_READ_ONLY') {
        setFormError(t('This Job Template is Linqage-managed and cannot be edited here.'));
        return;
      }
      if (err.status === 403) {
        setFormError(t("You don't have permission to author Job Templates."));
        return;
      }
      if (err.status === 409) {
        setFormError(err.message);
        return;
      }
    }
    if (err instanceof NetworkError) {
      setFormError(t("Couldn't reach the server. Check your connection and try again."));
      return;
    }
    setFormError(t('Something went wrong. Please try again.'));
  }

  function onCustomize() {
    if (template?.kind !== 'system') return;
    customize.mutate(
      { systemRowId: template.id },
      {
        onSuccess: ({ item: newItem }) => {
          void navigate(`/library/job-profiles/${encodeURIComponent(newItem.id)}`, {
            state: { customizedFrom: template.title },
            replace: true,
          });
        },
      },
    );
  }

  const isPending = create.isPending || update.isPending;
  const showBanner = !bannerDismissed && Boolean(navState?.customizedFrom);
  const headerTitle =
    mode === 'new' ? t('New Job Template') : (template?.title ?? t('Job Template'));
  const headerChip =
    mode === 'view' ? (
      <Chip variant="neutral" size="sm">
        {t('Linqage')}
      </Chip>
    ) : mode === 'edit' ? (
      <Chip variant="neutral" size="sm">
        {t('Yours')}
      </Chip>
    ) : null;

  return (
    <article className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTitleWrap}>
          <h1 className={styles.title}>{headerTitle}</h1>
          {headerChip}
        </div>
        <div className={styles.headerActions}>
          {mode === 'view' ? (
            <>
              <Button variant="ghost" size="md" onClick={backToList}>
                {t('Back')}
              </Button>
              {template?.customizedAsId ? (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() =>
                    void navigate(
                      `/library/job-profiles/${encodeURIComponent(template.customizedAsId!)}`,
                    )
                  }
                >
                  {t('Edit your copy →')}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={onCustomize}
                  isLoading={customize.isPending}
                >
                  {customize.isPending ? t('Customizing…') : t('Customize')}
                </Button>
              )}
              <Button variant="primary" size="md" onClick={() => setRunOpen(true)}>
                {t('Run')}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={backToList}
                disabled={isPending}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="submit"
                form={FORM_ID}
                variant="primary"
                size="md"
                isLoading={isPending}
              >
                {isPending ? t('Saving…') : t('Save')}
              </Button>
            </>
          )}
        </div>
      </header>

      {showBanner && navState?.customizedFrom ? (
        <Alert variant="info" onDismiss={() => setBannerDismissed(true)}>
          {t('Customized from "{name}". You can now edit it.', { name: navState.customizedFrom })}
        </Alert>
      ) : null}

      {template?.description ? <p className={styles.subtitle}>{template.description}</p> : null}

      {formError ? <Alert variant="error">{formError}</Alert> : null}

      <div className={styles.grid}>
        {/* ── Primary column ─────────────────────────────────────── */}
        <div className={styles.primary}>
          {mode === 'view' ? (
            <ViewFields template={template!} />
          ) : (
            <form
              id={FORM_ID}
              className={styles.form}
              onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
              noValidate
            >
              <TextField
                label={t('Title')}
                error={form.formState.errors.title?.message ?? fieldErrors.title}
                {...form.register('title')}
              />

              <FieldTextarea
                label={t('Description')}
                id="jt-description"
                rows={2}
                {...form.register('description')}
              />

              <FieldTextarea
                label={t('Instructions')}
                id="jt-instructions"
                rows={8}
                error={form.formState.errors.instructions?.message ?? fieldErrors.instructions}
                {...form.register('instructions')}
              />

              <FieldTextarea
                label={t('Inputs description')}
                id="jt-inputs-description"
                rows={3}
                placeholder={t('What inputs the Member should expect.')}
                {...form.register('inputsDescription')}
              />

              <FieldTextarea
                label={t('Outputs description')}
                id="jt-outputs-description"
                rows={3}
                placeholder={t('What the output should look like.')}
                {...form.register('outputsDescription')}
              />
            </form>
          )}
        </div>

        {/* ── Secondary column ───────────────────────────────────── */}
        <aside className={styles.secondary}>
          {template && mode === 'view' ? (
            <section className={styles.section}>
              <h3 className={styles.secondaryTitle}>{t('Defaults')}</h3>
              <DefaultLinks positions={template.defaultPositions} units={template.defaultUnits} />
            </section>
          ) : null}

          {template && mode === 'edit' ? (
            <section className={styles.section}>
              <h3 className={styles.secondaryTitle}>{t('Defaults')}</h3>
              <ChipMulti
                label={t('Positions')}
                hint={t('Roles this job suits — drives recommendations.')}
                options={(positionsCatalog.data?.items ?? []).map((p) => ({
                  id: p.id,
                  name: p.name,
                  description: p.description ?? undefined,
                }))}
                value={positionIds}
                onChange={setPositionIds}
                isLoading={positionsCatalog.isLoading}
                emptyLabel={t('No Positions yet.')}
              />
              <ChipMulti
                label={t('Departments')}
                hint={t('Departments this job belongs to.')}
                options={(unitsCatalog.data?.items ?? []).map((u) => ({
                  id: u.id,
                  name: u.name,
                  description: u.description ?? undefined,
                }))}
                value={unitIds}
                onChange={setUnitIds}
                isLoading={unitsCatalog.isLoading}
                emptyLabel={t('No Departments yet.')}
              />
            </section>
          ) : null}

          {mode === 'new' ? (
            <section className={styles.section}>
              <h3 className={styles.secondaryTitle}>{t('Reference files')}</h3>
              <p className={styles.sectionHelp}>
                {t('Save the template first, then come back to attach reference files.')}
              </p>
            </section>
          ) : template ? (
            <section className={styles.section}>
              <h3 className={styles.secondaryTitle}>{t('Reference files')}</h3>
              <p className={styles.sectionHelp}>
                {t('Files attached here are searchable by any Job created from this template.')}
              </p>
              <TemplateAttachmentsList templateId={template.id} readOnly={mode === 'view'} />
            </section>
          ) : null}

          {mode === 'view' && template ? <ViewFlags template={template} /> : null}
        </aside>
      </div>

      {mode === 'view' && template ? (
        <RunJobSheet
          open={runOpen}
          onOpenChange={setRunOpen}
          templateId={runOpen ? template.id : null}
        />
      ) : null}
    </article>
  );
}

const FORM_ID = 'job-template-editor-form';

/* ─── Sub-components ─────────────────────────────────────────────── */

type FieldTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  id: string;
  error?: string;
};

const FieldTextarea = (() => {
  return Object.assign(
    function FieldTextarea({ label, id, error, ...rest }: FieldTextareaProps) {
      return (
        <div className={styles.field}>
          <label htmlFor={id} className={styles.label}>
            {label}
          </label>
          <textarea id={id} className={styles.textarea} {...rest} />
          {error ? (
            <div className={styles.fieldError} role="alert">
              {error}
            </div>
          ) : null}
        </div>
      );
    },
    { displayName: 'FieldTextarea' },
  );
})();

function ViewFields({ template }: { template: TenantDetail | SystemDetail }) {
  return (
    <div className={styles.viewBody}>
      <ViewField label={t('Title')}>{template.title}</ViewField>
      {template.description ? (
        <ViewField label={t('Description')}>{template.description}</ViewField>
      ) : null}
      <ViewField label={t('Instructions')} prose>
        {template.instructions}
      </ViewField>
      {template.inputsDescription ? (
        <ViewField label={t('Inputs description')}>{template.inputsDescription}</ViewField>
      ) : null}
      {template.outputsDescription ? (
        <ViewField label={t('Outputs description')}>{template.outputsDescription}</ViewField>
      ) : null}
      {template.inputSchema ? (
        <ViewField label={t('Input schema')}>
          <pre className={styles.codeBlock}>{JSON.stringify(template.inputSchema, null, 2)}</pre>
        </ViewField>
      ) : null}
      {template.outputSchema ? (
        <ViewField label={t('Output schema')}>
          <pre className={styles.codeBlock}>{JSON.stringify(template.outputSchema, null, 2)}</pre>
        </ViewField>
      ) : null}
    </div>
  );
}

function ViewField({
  label,
  prose,
  children,
}: {
  label: string;
  prose?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      {prose ? (
        <pre className={styles.proseBlock}>{children}</pre>
      ) : typeof children === 'string' ? (
        <p className={styles.prose}>{children}</p>
      ) : (
        children
      )}
    </div>
  );
}

function ViewFlags({ template }: { template: TenantDetail | SystemDetail }) {
  return (
    <section className={styles.section}>
      <h3 className={styles.secondaryTitle}>{t('Advanced')}</h3>
      <dl className={styles.flagList}>
        <dt>{t('Validate inputs')}</dt>
        <dd>{template.validateInputs ? t('Yes') : t('No')}</dd>
        <dt>{t('Validate outputs')}</dt>
        <dd>{template.validateOutputs ? t('Yes') : t('No')}</dd>
        <dt>{t('Output validation mode')}</dt>
        <dd>{template.outputValidationMode}</dd>
      </dl>
    </section>
  );
}

/**
 * Reference-files list — upload + remove for tenant templates; read-only
 * for view mode. (System templates can still have reference files attached
 * by Linqage Ops; tenants can see but not modify them.)
 */
function TemplateAttachmentsList({
  templateId,
  readOnly,
}: {
  templateId: string;
  readOnly: boolean;
}) {
  const { data, isLoading, error } = useJobTemplateAttachments(templateId);
  const upload = useUploadAttachment();
  const link = useLinkJobTemplateAttachment(templateId);
  const unlink = useUnlinkJobTemplateAttachment(templateId);
  const inputRef = useRef<HTMLInputElement>(null);
  const isBusy = upload.isPending || link.isPending;

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        const { attachment } = await upload.mutateAsync({ file });
        await link.mutateAsync({ attachmentId: attachment.id });
      } catch (err) {
        console.error('Attachment link failed', err);
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  if (isLoading) return <p className={styles.attachmentsEmpty}>{t('Loading…')}</p>;
  if (error) return <p className={styles.attachmentsEmpty}>{t('Failed to load attachments.')}</p>;

  const items = data?.items ?? [];

  return (
    <>
      {items.length === 0 ? (
        <p className={styles.attachmentsEmpty}>{t('No reference files yet.')}</p>
      ) : (
        <ul className={styles.attachmentsList}>
          {items.map((a) => (
            <li key={a.id} className={styles.attachmentsItem}>
              <span className={styles.attachmentsName}>{a.name}</span>
              <span className={styles.attachmentsMeta}>
                {a.mime_type} · {formatBytes(a.size_bytes)}
              </span>
              {!readOnly ? (
                <button
                  type="button"
                  className={styles.attachmentsRemove}
                  onClick={() => unlink.mutate({ attachmentId: a.id })}
                  disabled={unlink.isPending}
                >
                  {t('Remove')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {!readOnly ? (
        <div className={styles.attachmentsUploadRow}>
          <input
            ref={inputRef}
            type="file"
            multiple
            className={styles.attachmentsInput}
            onChange={(e) => void handleFiles(e.target.files)}
            disabled={isBusy}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={isBusy}
          >
            {isBusy ? t('Uploading…') : t('Upload')}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
